import React, { useMemo, useState } from 'react';

import { Alert, Stack } from '@mui/material';

import { useGetCMChecklistManualOverrides } from '~/hooks/apis/offers/useCMChecklistManualOverrides';
import type {
  OpportunityRelatedBundledExperience,
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
  OpportunityRelatedInclusion,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import ChecklistActiveTab from './ChecklistActiveTab';
import ChecklistToolbar from './ChecklistToolbar';
import ContextCard from './ContextCard';
import { isRowApproved } from './approvalQueueModel';
import { SETUP_CATEGORY, getChecklistCategory } from './checklistCategories';
import { getAllChecklistRows } from './checklistRows';
import type { SetupRuleInput } from './tabs/setup/types';
import type { BlackoutDateRange } from './tabs/surcharges/types';
import type { ChecklistRowsByCategory, ChecklistTabId } from './types';
import { useActiveCategoryRows } from './useActiveCategoryRows';
import { useApprovalQueueModel } from './useApprovalQueueModel';
import { useCMChecklistRows } from './useCMChecklistRows';
import { useChecklistApprovalState } from './useChecklistApprovalState';
import { useChecklistSummary } from './useChecklistSummary';
import { useReconcileStaleCMChecklistManualOverrides } from './useReconcileStaleCMChecklistManualOverrides';
import { useVisibleChecklistRows } from './useVisibleChecklistRows';

interface Props {
  blackoutDates?: Array<BlackoutDateRange>;
  bookByDate?: string;
  campaignType?: string;
  channelManager?: string;
  hotelName: string;
  relatedInclusions?: Array<OpportunityRelatedInclusion>;
  isLoadingProperty: boolean;
  isLoadingSetup: boolean;
  isLoadingSurcharges: boolean;
  offer?: App.Offer;
  offerId: string;
  opportunity?: App.OpportunityPage;
  opportunityContract?: App.Contract;
  opportunityError?: unknown;
  packages?: Array<App.Package>;
  propertyId?: string;
  propertyError?: unknown;
  propertyHasTaxesPayableAtProperty: boolean;
  propertyRoomTypes?: Array<App.RoomType>;
  relatedBundledExperiences?: Array<OpportunityRelatedBundledExperience>;
  relatedDeals?: Array<OpportunityRelatedDeal>;
  relatedExtraGuestSurcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  setupInput: SetupRuleInput;
  surcharges: Array<SalesforceSurchargePeriod>;
  surchargesError?: unknown;
  travelFromDate?: string;
  travelToDate?: string;
  vendorId?: string;
}

export default function CMChecklistWorkspace({
  blackoutDates,
  bookByDate,
  campaignType,
  channelManager,
  hotelName,
  relatedInclusions,
  isLoadingProperty,
  isLoadingSetup,
  isLoadingSurcharges,
  offer,
  offerId,
  opportunity,
  opportunityContract,
  opportunityError,
  packages,
  propertyId,
  propertyError,
  propertyHasTaxesPayableAtProperty,
  propertyRoomTypes,
  relatedBundledExperiences,
  relatedDeals,
  relatedExtraGuestSurcharges,
  setupInput,
  surcharges,
  surchargesError,
  travelFromDate,
  travelToDate,
  vendorId,
}: Props) {
  const [showAutoApproved, setShowAutoApproved] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabId, setActiveTabId] = useState<ChecklistTabId>('approvalQueue');
  const manualOverridesQuery = useGetCMChecklistManualOverrides(offerId);
  const {
    baseSurchargesRows,
    blackoutCoverage,
    blackoutRows,
    bundledExperienceRows,
    campaignRows,
    capacityRows,
    categorySyncedAt,
    discountsMarginRows,
    dealOptionsInclusionsDealRows,
    dealOptionsInclusionsRows,
    marginRows,
    opportunityTravelFrom,
    opportunityTravelTo,
    setupRows,
    surchargeTableRows,
    taxesPaymentsRows,
  } = useCMChecklistRows({
    blackoutDates,
    bookByDate,
    offer,
    opportunity,
    opportunityContract,
    opportunityError,
    packages,
    propertyError,
    propertyId,
    propertyHasTaxesPayableAtProperty,
    propertyRoomTypes,
    relatedBundledExperiences,
    relatedDeals,
    relatedExtraGuestSurcharges,
    relatedInclusions,
    setupInput,
    surcharges,
    travelFromDate,
    travelToDate,
  });
  const checklistRows = useMemo<ChecklistRowsByCategory>(
    () => ({
      baseSurchargesRows,
      blackoutRows,
      bundledExperienceRows,
      campaignRows,
      capacityRows,
      discountsMarginRows,
      dealOptionsInclusionsRows,
      marginRows,
      setupRows,
      surchargeTableRows,
      taxesPaymentsRows,
    }),
    [
      baseSurchargesRows,
      blackoutRows,
      bundledExperienceRows,
      campaignRows,
      capacityRows,
      discountsMarginRows,
      dealOptionsInclusionsRows,
      marginRows,
      setupRows,
      surchargeTableRows,
      taxesPaymentsRows,
    ],
  );
  const allChecklistRows = useMemo(() => getAllChecklistRows(checklistRows), [checklistRows]);
  const {
    approveRow,
    expandedQueueItems,
    expandVisibleQueueItems,
    manualOverrides,
    markRowsApproved,
    toggleQueueItemDetails,
    unapproveRow,
  } = useChecklistApprovalState({
    offerId,
    manualOverridesFromApi: manualOverridesQuery.data ?? [],
    rows: allChecklistRows,
  });

  const isApprovalQueueMode = activeTabId === 'approvalQueue';
  const activeCategory = isApprovalQueueMode ? SETUP_CATEGORY : getChecklistCategory(activeTabId);

  const visibleRows = useVisibleChecklistRows({
    activeCategoryId: activeCategory.id,
    isApprovalQueueMode,
    manualOverrides,
    searchQuery,
    showAutoApproved,
    ...checklistRows,
  });
  const { reportSummaries, visibleQueueItems } = useApprovalQueueModel({
    blackoutCoverage,
    manualOverrides,
    offerId,
    propertyId,
    rows: checklistRows,
    vendorId,
    visibleRows,
  });

  const { activeExpandableItems, activeTabRows } = useActiveCategoryRows({
    activeCategoryId: activeCategory.id,
    baseSurchargesRows,
    blackoutRows,
    bundledExperienceRows,
    campaignRows,
    capacityRows,
    dealOptionsInclusionsDealRows,
    dealOptionsInclusionsRows,
    discountsMarginRows,
    isApprovalQueueMode,
    marginRows,
    setupRows,
    surchargeTableRows,
    taxesPaymentsRows,
    visibleQueueItems,
    visibleRows,
  });

  const areActiveTabDetailsExpanded =
    activeExpandableItems.length > 0 &&
    activeExpandableItems.every((item) => expandedQueueItems[item.id] ?? item.defaultOpen ?? false);
  const summary = useChecklistSummary({
    baseSurchargesRows,
    campaignRows,
    discountsMarginRows,
    blackoutRows,
    bundledExperienceRows,
    taxesPaymentsRows,
    capacityRows,
    dealOptionsInclusionsRows,
    manualOverrides,
    marginRows,
    setupRows,
    surchargeTableRows,
  });
  const isLoading = isLoadingProperty || isLoadingSetup || isLoadingSurcharges;
  const hasError = !!propertyError || !!surchargesError;

  useReconcileStaleCMChecklistManualOverrides({
    enabled: !isLoading && !hasError && manualOverridesQuery.isSuccess,
    offerId,
    manualOverridesFromApi: manualOverridesQuery.data ?? [],
    rows: allChecklistRows,
  });

  if (hasError) {
    return <Alert severity="error">Unable to load the CM checklist data.</Alert>;
  }

  if (isLoading) {
    return <Alert severity="info">Loading CM checklist data.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <ContextCard
        approvedCount={summary.approvedCount}
        bookByDate={bookByDate}
        campaignType={campaignType}
        channelManager={channelManager}
        hotelName={hotelName}
        needsApprovalCount={summary.needsApprovalCount}
        onSelectApprovalQueue={() => setActiveTabId('approvalQueue')}
        offerId={offerId}
        travelToDate={travelToDate}
        totalCount={summary.totalCount}
      />

      <ChecklistToolbar
        activeTabId={activeTabId}
        areVisibleQueueItemsExpanded={areActiveTabDetailsExpanded}
        categoryCounts={summary.categoryCounts}
        isApprovalQueueMode={isApprovalQueueMode}
        needsApprovalCount={summary.needsApprovalCount}
        searchQuery={searchQuery}
        showAutoApproved={showAutoApproved}
        visibleQueueItemCount={activeExpandableItems.length}
        onExpandVisibleCards={() => expandVisibleQueueItems(activeExpandableItems)}
        onMarkAllApproved={!isApprovalQueueMode ? () => markRowsApproved(activeTabRows) : undefined}
        onMarkAllApprovedDisabled={activeTabRows
          .filter((row) => row.status !== 'blocked')
          .every((row) => isRowApproved(row, manualOverrides))}
        onSearchQueryChange={setSearchQuery}
        onShowAutoApprovedChange={() => setShowAutoApproved((current) => !current)}
        onTabChange={setActiveTabId}
        syncedAt={!isApprovalQueueMode ? categorySyncedAt[activeCategory.id] : undefined}
      />

      <ChecklistActiveTab
        activeCategory={activeCategory}
        blackoutCoverage={blackoutCoverage}
        dealOptionsInclusionsDealRows={dealOptionsInclusionsDealRows}
        expandedQueueItems={expandedQueueItems}
        isApprovalQueueMode={isApprovalQueueMode}
        manualOverrides={manualOverrides}
        needsApprovalCount={summary.needsApprovalCount}
        opportunityError={opportunityError}
        reportSummaries={reportSummaries}
        rows={checklistRows}
        showAutoApproved={showAutoApproved}
        travelFrom={opportunityTravelFrom}
        travelTo={opportunityTravelTo}
        visibleQueueItems={visibleQueueItems}
        visibleRows={visibleRows}
        onApprovalChange={approveRow}
        onSelectTab={setActiveTabId}
        onToggleDetails={toggleQueueItemDetails}
        onUnapprovalChange={unapproveRow}
      />
    </Stack>
  );
}
